import { __decorate } from "tslib";
// NPM
import { Component, Mixins } from "vue-property-decorator";
import { setInteractionMode } from "vee-validate";
// SYSTEM1
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header.vue";
import BaseMixer from "@/router/views/BaseMixer";
import StrictDateMixin from "@/router/views/StrictDateMixin";
import UnsavedChangesMixin from "@/router/views/UnsavedChangesMixin";
import { toaster } from "@/utilities/toaster";
import { checkImageFile } from "@/utilities/imageFileCheck";
import Loader from "../../../components/widgets/loader.vue";
import SectionLoader from "../../../components/widgets/SectionLoader.vue";
import { ClientModel } from "@/store/models/client/client";
import ClientModule from "@/store/modules/client.module";
import { AddressModel } from "@/store/models/shared/address";
import { DateFormatConstant } from "@/store/DateFormatConstants";
import { isIndia, RECHECK_MODE } from "@/types/validators";
/**
 * Add Client Components
 */
setInteractionMode("passive");
Component.registerHooks(["metaInfo"]);
let AddClient = /** @class */ (() => {
    let AddClient = class AddClient extends Mixins(BaseMixer, StrictDateMixin, UnsavedChangesMixin) {
        constructor() {
            // DATA
            super(...arguments);
            this.pageIsLoading = true;
            this.isSaving = false;
            this.ctaButtons = [{ routeName: "Clients" }];
            // The picked logo stays local (preview only) until Save uploads it
            this.pendingLogo = null;
            this.uploadedLogoFile = null;
            this.uploadedLogoId = "";
            this.previewImage = null;
            this.recheckMode = RECHECK_MODE;
            this.title = "Add Client";
            this.items = [
                {
                    text: "Home",
                    to: "/dashBoard",
                },
                {
                    text: "PMO",
                },
                {
                    text: "Clients",
                    to: "/clients",
                },
                {
                    text: "Add Client",
                    active: true,
                },
            ];
            this.client = new ClientModel();
            this.address = new AddressModel();
        }
        // Compares calendar days only; PSA Date may equal the Start Date.
        isPsaDateBeforeStartDate() {
            if (!this.client.startDate || !this.client.psaDate) {
                return false;
            }
            const startDate = new Date(this.client.startDate);
            const psaDate = new Date(this.client.psaDate);
            startDate.setHours(0, 0, 0, 0);
            psaDate.setHours(0, 0, 0, 0);
            return psaDate < startDate;
        }
        // Compares calendar days only; SS Date may equal the Start Date.
        isServiceStatementDateBeforeStartDate() {
            if (!this.client.startDate || !this.client.serviceStatementDate) {
                return false;
            }
            const startDate = new Date(this.client.startDate);
            const serviceStatementDate = new Date(this.client.serviceStatementDate);
            startDate.setHours(0, 0, 0, 0);
            serviceStatementDate.setHours(0, 0, 0, 0);
            return serviceStatementDate < startDate;
        }
        // SS Date / PSA Date pickers: no days before the Start Date (same day allowed)
        disableBeforeStartDate(date) {
            if (!this.client.startDate) {
                return false;
            }
            const startDate = new Date(this.client.startDate);
            startDate.setHours(0, 0, 0, 0);
            return date < startDate;
        }
        saveClient() {
            this.isSaving = true;
            this.validate("newClientObserver").then((response) => {
                if (response) {
                    if (this.hasDateErrors) {
                        // The rejected date's message is already shown under its picker
                        this.isSaving = false;
                    }
                    else if (this.isServiceStatementDateBeforeStartDate()) {
                        this.isSaving = false;
                        toaster.ShowError("SS Date should be the same as or later than the Start Date.");
                    }
                    else if (this.isPsaDateBeforeStartDate()) {
                        this.isSaving = false;
                        toaster.ShowError("PSA Date should be the same as or later than the Start Date.");
                    }
                    else {
                        this.uploadPendingLogo().then((uploaded) => {
                            if (uploaded) {
                                this.submitClient();
                            }
                            else {
                                this.isSaving = false;
                            }
                        });
                    }
                }
                else {
                    this.isSaving = false;
                }
            });
        }
        // Uploads the picked logo (if any) and keeps its id; false when the upload failed and the client must not be saved.
        // A logo already uploaded by an earlier, failed save isn't uploaded again.
        uploadPendingLogo() {
            if (!this.pendingLogo || (this.pendingLogo === this.uploadedLogoFile && this.uploadedLogoId)) {
                return Promise.resolve(true);
            }
            const logo = this.pendingLogo;
            const formData = new FormData();
            formData.append("file", logo);
            return ClientModule.UploadClientPicture({ file: formData, type: "logos" }).then((res) => {
                if (res) {
                    this.uploadedLogoFile = logo;
                    this.uploadedLogoId = res.mediaFileId;
                    return true;
                }
                toaster.ShowError("Client logo upload failed.");
                return false;
            }, () => false);
        }
        submitClient() {
            this.client.name = (this.client.name || "").trim();
            this.client.clientLogoId = this.pendingLogo ? this.uploadedLogoId : null;
            this.client.addresses = [];
            this.client.addresses.push(this.address);
            if (this.dateFormat === DateFormatConstant.DateFormat.DateFormatMDY()) {
                this.client.startDateAsString = this.client.startDate ? this.dateFormattedString(this.client.startDate) : "";
                this.client.serviceStatementDateAsString = this.client.serviceStatementDate ? this.dateFormattedString(this.client.serviceStatementDate) : "";
                this.client.sowDateAsString = this.client.sowDate ? this.dateFormattedString(this.client.sowDate) : "";
                this.client.psaDateAsString = this.client.psaDate ? this.dateFormattedString(this.client.psaDate) : "";
            }
            else {
                this.client.startDateAsString = this.client.startDate ? this.dateFormatted(new Date(this.client.startDate)) : "";
                this.client.serviceStatementDateAsString = this.client.serviceStatementDate ? this.dateFormatted(new Date(this.client.serviceStatementDate)) : "";
                this.client.sowDateAsString = this.client.sowDate ? this.dateFormatted(new Date(this.client.sowDate)) : "";
                this.client.psaDateAsString = this.client.psaDate ? this.dateFormatted(new Date(this.client.psaDate)) : "";
            }
            ClientModule.SaveClient(this.client)
                .then((res) => {
                if (res) {
                    toaster.ShowSuccess("Client was saved successfully.");
                    this.skipUnsavedCheck();
                    this.$router.push({ name: "ClientDetails", params: { clientId: ClientModule.clientId } });
                }
                else {
                    toaster.ShowError("Client saving failed.");
                }
            })
                .finally(() => {
                this.isSaving = false;
            });
        }
        //Methods
        // Checks the file's content and keeps it for Save; nothing is uploaded yet
        fileSelected2(e) {
            const input = e.target;
            const newFiles = input.files || e.dataTransfer.files;
            if (!newFiles.length)
                return;
            const file = newFiles[0];
            checkImageFile(file).then((error) => {
                if (error) {
                    // Clear the input so picking the same file again re-runs the check
                    input.value = "";
                    toaster.ShowError(error);
                    return;
                }
                this.pendingLogo = file;
                const reader = new FileReader();
                reader.onload = () => {
                    this.previewImage = reader.result;
                };
                reader.readAsDataURL(file);
            });
        }
        // The values that count as unsaved edits (see UnsavedChangesMixin)
        unsavedState() {
            return {
                client: this.client,
                address: this.address,
                logo: this.pendingLogo ? `${this.pendingLogo.name}:${this.pendingLogo.size}` : null,
            };
        }
        // Computed
        get countryLists() {
            return ClientModule.countryLists;
        }
        get selectedCountryName() {
            const country = this.countryLists.find((x) => x.id.toString() === String(this.address.countryId));
            return country ? country.value : "";
        }
        get zipLabel() {
            return isIndia(this.selectedCountryName) ? "PIN Code" : "Zipcode";
        }
        get eligibleCSMs() {
            return ClientModule.eligibleCSMs;
        }
        get eligibleTSMs() {
            return ClientModule.eligibleTSMs;
        }
        get stateLists() {
            if (this.address.countryId != null) {
                return ClientModule.stateLists.filter((x) => x.countryId == this.address.countryId);
            }
            return [];
        }
        //Lifecycle
        created() {
            var promises = [ClientModule.GetEligibleClientManagers()];
            if (ClientModule.countryLists.length === 0) {
                promises.push(ClientModule.GetCountryStateLists());
            }
            Promise.all(promises).then(() => {
                this.pageIsLoading = false;
            }, () => {
                this.pageIsLoading = false;
            });
        }
    };
    AddClient = __decorate([
        Component({
            name: "AddClient",
            components: {
                Layout,
                PageHeader,
                Loader,
                SectionLoader,
            },
        })
    ], AddClient);
    return AddClient;
})();
export default AddClient;
